// Buybox controller — ONE instance per <nss-buybox> parent block.
//
// Owns the buybox state and all shared behavior that used to run once per
// variant per child block in the pane script (nss-buybox-pane.js), back
// when it was a per-variant god component. Each variant renders its own
// product form, and those form inputs remain the source
// of truth ([name="id"], [name="quantity"], [name="selling_plan"]) —
// Shopify's ATC submit reads them — so the controller resolves them per
// variant via formEls(). It is the single delegated click handler
// for variant links, the quantity stepper, one-time/subscription toggles,
// selling-plan radios, and the one-time ATC button, and owns the
// section-wide display fan-out, discount preview (window.NSS_DISCOUNT),
// URL sync, and the buyboxUpdate dispatch.
//
// nss-buybox-pane (see nss-buybox-pane.js) is now a pure
// visibility pane: it only toggles nss-active on buyboxUpdate. Satellites
// (gallery, sticky ATC, facts, …) keep their own elements and keep listening
// to buyboxUpdate — the event name and detail shape are unchanged.
let elementName = `nss-buybox`;
if (typeof customElements.get(elementName) === `undefined`) {
  class NSSBuybox extends HTMLElement {
    constructor() {
      super();

      this.debug = false;
    }

    init() {
      // Display fan-out must reach blocks OUTSIDE this element (sticky ATC).
      this.sectionScope = this.closest(`[id^="shopify-section"]`) || document;
      this.currencyFormatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      });

      const activePane = this.querySelector(`nss-buybox-pane.nss-active`);
      this.currentId = activePane ? activePane.getAttribute(`nss-variant-id`) : ``;
      this._baseStandalone = {};

      // Remember each variant's selected plan so the subscription toggle can
      // restore it after a one-time toggle clears the form's selling_plan.
      this._planByVariant = {};
      this.sectionScope.querySelectorAll(`[name="selling_plan"]`).forEach((select) => {
        const wrapper = select.closest(`[nss-variant-id]`);
        if (wrapper) {
          this._planByVariant[wrapper.getAttribute(`nss-variant-id`)] = select.value;
        }
      });
    }

    connectedCallback() {
      if (this.debug) console.log(`[NSS][Buybox] Connected.`);

      this.init();

      this.addEventListener(`click`, this.clickHandler.bind(this));
      // buyboxUpdate doesn't bubble (it's dispatched directly on each
      // [nss-buybox-update] element) — a capture-phase listener still sees it
      // on the way down to our own panes, which also scopes us to our subtree.
      this.addEventListener(`buyboxUpdate`, this.captureUpdate.bind(this), true);
      this.boundNavigate = this.navigateHandler.bind(this);
      window.navigation?.addEventListener(`navigate`, this.boundNavigate);

      this.initQuantity();
    }

    disconnectedCallback() {
      window.navigation?.removeEventListener(`navigate`, this.boundNavigate);
    }

    /* ----------------------------
       Helpers
    ---------------------------- */

    // The variant's own product form inputs — the buybox's source of truth.
    formEls(id) {
      const q = (sel) => this.sectionScope.querySelector(`[nss-variant-id="${id}"] ${sel}`);
      return {
        form: q(`.shopify-product-form`),
        productSelect: q(`[name="id"]`),
        quantity: q(`[name="quantity"]`),
        planSelect: q(`[name="selling_plan"]`),
      };
    }

    qtyBounds(quantityEl) {
      const bounds = { min: 1, max: 15 };
      if (quantityEl) {
        const inventory = quantityEl.getAttribute(`nss-inventory-qty`);
        bounds.max = Math.min(15, inventory);
        /**
         * We don't need to use the inventory policy here because we will always
         * deny - either due to lack of inventory (< 15) or at 15 total.
         */
      }
      return bounds;
    }

    // The pane containing a clicked control decides which variant's form the
    // interaction belongs to; controls outside any pane act on the current.
    variantOf(target) {
      const pane = target.closest(`nss-buybox-pane`);
      return pane ? pane.getAttribute(`nss-variant-id`) : this.currentId;
    }

    // Section-wide display hooks scoped to one variant (spans sibling blocks
    // and the sticky ATC outside this element).
    selFor(id, hook) {
      return this.sectionScope.querySelectorAll(`[nss-variant-id="${id}"] ${hook}`);
    }

    formatCurrency(value) {
      const formatted = this.money2(value);

      // Remove ".00" if it's at the end
      if (formatted.endsWith(".00")) {
        return formatted.slice(0, -3);
      }

      return formatted;
    }

    money2(value) {
      return new Intl.NumberFormat(`en-US`, {
        style: `currency`,
        currency: `USD`,
      }).format(value);
    }

    formatPerDay(perUnitCents, supplyDays) {
      return `${this.money2(Math.round(perUnitCents / supplyDays) / 100)} / Day`;
    }

    // Keep the dynamic "Ships every N months" Autoship benefit in sync with the
    // selected frequency. Pulls the leading number out of the frequency heading
    // (e.g. "3 Months" -> "3"). No-op when the span isn't present (other templates)
    // or the heading has no number.
    updateShipsEveryMonths(id, freqName) {
      if (!freqName) return;
      const match = freqName.match(/\d+/);
      if (!match) return;
      this.selFor(id, `[nss-selected-ships-months]`).forEach((el) => {
        el.textContent = match[0];
      });
    }

    /* ----------------------------
       Variant switching + URL
    ---------------------------- */

    updateEvent(newId) {
      if (this.debug) console.log(`[NSS][Buybox] updateEvent - newId:`, newId);

      const els = this.formEls(this.currentId);
      // document-wide, not this.sectionScope: satellite blocks that live in a
      // separate Shopify section from the buybox (e.g. nss-product-details'
      // About/tasting-notes content, further down the page) need this event
      // too. Safe to broaden — each receiving element (nss-buybox-pane.js,
      // nss-buybox-bottom above) already re-scopes itself to its own nearest
      // nss-buybox/section and ignores the update unless a variant id match
      // exists there, so an unrelated buybox elsewhere on the page (e.g. a
      // recommendation carousel) won't react to a different product's switch.
      const updateEls = document.querySelectorAll(`[nss-buybox-update]`);
      this._dispatching = true;
      updateEls.forEach((updateEl) => {
        const detail = {
          oldId: this.currentId,
          newId: newId,
          sellingPlanId: els.planSelect ? els.planSelect.value : null,
          quantity: els.quantity ? els.quantity.value : null,
        };
        updateEl.dispatchEvent(new CustomEvent("buyboxUpdate", { detail: detail }));
      });
      this._dispatching = false;
    }

    switchVariant(newId) {
      this.updateEvent(newId);
      this.currentId = newId;
      this.updateURL();
      this.applyDiscountPreview();
    }

    // A satellite switched the variant on its own (none do today — the sticky
    // ATC only listens — but the bus allows it): follow along. Self-originated
    // dispatches are excluded via the _dispatching flag, and cross-buybox
    // events never reach us because capture only descends into our own panes.
    captureUpdate(e) {
      if (this._dispatching) return;
      const newId = e.detail && e.detail.newId;
      if (!newId || newId === this.currentId) return;
      this.currentId = newId;
      this.updateURL();
      this.applyDiscountPreview();
    }

    navigateHandler(e) {
      if (this.debug) console.log(`[NSS][Buybox] Navigation event, e:`, e);

      if (e.navigationType === "traverse") {
        const url = new URL(e.destination.url);
        const newId = url.searchParams.get(`variant`);
        if (
          newId &&
          newId !== this.currentId &&
          this.querySelector(`nss-buybox-pane[nss-variant-id="${newId}"]`)
        ) {
          this.updateEvent(newId);
          this.currentId = newId;
          this.applyDiscountPreview();
        }
      }
    }

    updateURL() {
      if (!window.location.pathname.includes("/products/")) {
        return;
      }

      // Start from the current URL so existing query params (utm_*, ref, etc.)
      // are preserved — only the variant (and the product path, for a switch)
      // should change.
      const pane = this.querySelector(`nss-buybox-pane[nss-variant-id="${this.currentId}"]`);
      const variantUrl = pane && pane.getAttribute(`nss-variant-url`);
      const target = new URL(window.location.href);
      if (variantUrl) {
        const src = new URL(variantUrl, window.location.href);
        target.pathname = src.pathname;
        target.searchParams.set(`variant`, src.searchParams.get(`variant`) || this.currentId);
      } else {
        target.searchParams.set(`variant`, this.currentId);
      }

      // Only push when the destination actually differs — otherwise repeated
      // switches to the same variant stack identical entries onto the history
      // (and break the back button).
      const current = new URL(window.location.href);
      if (
        current.pathname === target.pathname &&
        current.searchParams.get(`variant`) === target.searchParams.get(`variant`)
      ) {
        return;
      }

      window.history.pushState({}, "", target);
    }

    /* ----------------------------
       Selling-plan selection
    ---------------------------- */

    // Bootstrap displays from each pane's pre-checked plan option on load
    // (panes without plan options no-op). Only the active pane re-announces.
    initQuantity() {
      this.querySelectorAll(`nss-buybox-pane`).forEach((pane) => {
        const plan = pane.querySelector(`[nss-selling-plan-id]:checked`);
        if (plan) this.applyPlanSelection(pane, plan, { bootstrap: true });
      });
    }

    // Shared body of the selling-plan radio click branch and the per-pane
    // bootstrap. `pane` is the nss-buybox-pane containing `planEl`.
    applyPlanSelection(pane, planEl, opts = {}) {
      const paneId = pane.getAttribute(`nss-variant-id`);
      const els = this.formEls(paneId);

      const id = planEl.getAttribute(`nss-selling-plan-id`);
      const name = planEl.getAttribute(`nss-selling-plan-name`);
      const price = planEl.getAttribute(`data-price`);
      const compareAtPrice = planEl.getAttribute(`data-compare-at-price`);
      const planName = planEl.getAttribute(`nss-plan-name`);
      const dataPerDay = planEl.getAttribute(`data-per-day`);
      const dataSaving = planEl.getAttribute(`data-saving`);
      const dataCount = planEl.getAttribute(`data-count`);
      const dataMostPopular = planEl.getAttribute(`data-most-popular`);
      const dataOTPPrice = planEl.getAttribute(`data-otp-price`);
      const dataOTPCompareAtPrice = planEl.getAttribute(`data-otp-compare-at-price`);
      const dataOTPPerDay = planEl.getAttribute(`data-otp-per-day`);

      if (this.debug) console.log(`[NSS][Buybox] plan id:`, id);

      if (!opts.bootstrap) {
        pane.querySelectorAll(`[nss-selling-plan-id]`).forEach((el) => {
          el.parentElement.classList.remove(`nss-active`);
        });
        planEl.parentElement.classList.add(`nss-active`);
      }

      if (els.planSelect) els.planSelect.value = id;
      this._planByVariant[paneId] = id;

      // Legacy variant-per-frequency: the option may carry its own variant id;
      // point the form's variant at it so ATC/OTP add the resolved variant.
      const variantId = planEl.getAttribute(`data-variant-id`);
      if (variantId && els.productSelect) {
        els.productSelect.value = variantId;
      }

      if (!opts.bootstrap) {
        const planMenu = pane.querySelector(`[nss-plan-select]`);
        if (planMenu) {
          planMenu.parentElement.classList.remove(`nss-active`);
        }
      }
      const selectedPlanName = this.sectionScope.querySelector(
        `[nss-variant-id="${paneId}"] [nss-selected-plan-name]`
      );
      if (selectedPlanName) {
        selectedPlanName.innerHTML = name;
      }

      if (planEl.hasAttribute(`nss-frequency-name`)) {
        const freqName = planEl.getAttribute(`nss-frequency-name`);
        this.selFor(paneId, `[nss-selected-frequency-name]`).forEach((el) => {
          el.innerHTML = freqName;
        });
        this.updateShipsEveryMonths(paneId, freqName);
        if (planEl.hasAttribute("data-quantity") && els.quantity) {
          els.quantity.value = parseInt(planEl.getAttribute("data-quantity"), 10);
        }

        this.selFor(paneId, `[nss-selected-plan-most-popular]`).forEach((el) => {
          el.innerHTML = dataMostPopular ? dataMostPopular : "";
          el.style.display = dataMostPopular ? "block" : "none";
        });
        this.selFor(paneId, `[nss-selected-plan-name-title]`).forEach((el) => {
          el.innerHTML = planName ? planName : "";
          el.style.display = planName ? "block" : "none";
        });
        this.selFor(paneId, `[nss-selling-count]`).forEach((el) => {
          el.innerHTML = dataCount ? dataCount : "";
          el.style.display = dataCount ? "block" : "none";
        });
        this.selFor(paneId, `[nss-selling-serving]`).forEach((el) => {
          el.innerHTML = dataPerDay ? dataPerDay : "";
          el.style.display = dataPerDay ? "block" : "none";
        });
        this.selFor(paneId, `[nss-selling-saving-ammount]`).forEach((el) => {
          el.innerHTML = dataSaving ? dataSaving : "";
          el.style.display = dataSaving ? "block" : "none";
        });
        this.selFor(paneId, `[nss-selected-otp-compare-at-price]`).forEach((item) => {
          item.innerText =
            dataOTPCompareAtPrice && els.quantity
              ? this.currencyFormatter.format(
                  (els.quantity.value * parseInt(dataOTPCompareAtPrice, 10)) / 100
                )
              : "";
        });
        this.selFor(paneId, `[nss-selected-otp-price]`).forEach((item) => {
          item.innerText =
            dataOTPPrice && els.quantity
              ? this.currencyFormatter.format(
                  (els.quantity.value * parseInt(dataOTPPrice, 10)) / 100
                )
              : "";
        });
        this.selFor(paneId, `[nss-selected-otp-per-day]`).forEach((item) => {
          item.innerText = dataOTPPerDay
            ? this.formatCurrency(parseInt(dataOTPPerDay, 10) / 100)
            : "";
        });

        if (!opts.bootstrap) {
          this.updatePrice(pane, paneId, compareAtPrice, price);
          this.updateEvent(paneId);
          this.currentId = paneId;
          this.updateURL();
          this.applyDiscountPreview();
        }
      }

      if (opts.bootstrap) {
        this.updatePrice(pane, paneId, compareAtPrice, price);
        if (pane.classList.contains(`nss-active`)) {
          this.updateEvent(paneId);
          this.applyDiscountPreview();
        }
      }
    }

    updatePrice(pane, paneId, subCompareAtPrice, subPrice) {
      const els = this.formEls(paneId);
      pane.querySelectorAll(`[nss-selected-frequency-compare-at-price]`).forEach((item) => {
        item.setAttribute("data-price", subCompareAtPrice);
      });
      this.selFor(paneId, `[nss-selected-frequency-price]`).forEach((item) => {
        item.setAttribute("data-price", subPrice);
      });
      this.sectionScope
        .querySelectorAll(
          `[nss-variant-id="${paneId}"] [nss-selected-frequency-compare-at-price],[nss-variant-id="${paneId}"] [nss-selected-frequency-price]`
        )
        .forEach((item) => {
          const price = item.getAttribute("data-price");
          if (price && els.quantity) {
            item.innerText = this.currencyFormatter.format(
              (els.quantity.value * parseInt(price, 10)) / 100
            );
          } else {
            item.innerText = "";
          }
        });
    }

    /* ----------------------------
       Click delegation
    ---------------------------- */

    clickHandler(e) {
      if (this.debug) console.log(`[NSS][Buybox] Click - e.target:`, e.target);

      if (
        e.target.hasAttribute(`nss-add-to-cart-btn-otp`) ||
        e.target.closest(`[nss-add-to-cart-btn-otp]`)
      ) {
        this.atcBtnOTP(this.variantOf(e.target));
        return;
      }
      if (
        e.target.hasAttribute(`nss-add-to-cart-btn`) ||
        e.target.closest(`[nss-add-to-cart-btn]`)
      ) {
        return;
      }

      if (e.target.hasAttribute(`nss-variant-id`)) {
        e.preventDefault();
        const newId = e.target.getAttribute(`nss-variant-id`);
        if (this.debug) console.log(`[NSS][Buybox] newId:`, newId);
        this.switchVariant(newId);
      }

      if (e.target.hasAttribute(`nss-plan-select`)) {
        e.target.parentElement.classList.toggle(`nss-active`);
      }

      if (e.target.hasAttribute(`nss-selling-plan-id`)) {
        const pane = e.target.closest(`nss-buybox-pane`);
        if (pane) this.applyPlanSelection(pane, e.target);
      }

      // The branches below are independent of the selling-plan radio handling
      // above: OTP/subscription toggles and the quantity stepper must fire for
      // clicks that carry only their own attribute.
      if (e.target.hasAttribute(`nss-otp`)) {
        const id = this.variantOf(e.target);
        const els = this.formEls(id);
        if (els.planSelect) {
          els.planSelect.value = "";
          const name = e.target.getAttribute(`nss-frequency-name`);
          this.selFor(id, `[nss-selected-frequency-name]`).forEach((el) => {
            el.innerHTML = name;
          });
          const offerName = e.target.getAttribute(`nss-offer-name`);
          this.selFor(id, `[nss-selected-offer-name]`).forEach((el) => {
            el.innerHTML = offerName;
          });
          this.updateEvent(id);
          this.applyDiscountPreview();
        }
      }

      if (e.target.hasAttribute(`nss-sub`)) {
        const id = this.variantOf(e.target);
        const els = this.formEls(id);
        if (els.planSelect) {
          els.planSelect.value = this._planByVariant[id] || "";
          const name = e.target.getAttribute(`nss-frequency-name`);
          this.selFor(id, `[nss-selected-frequency-name]`).forEach((el) => {
            el.innerHTML = name;
          });
          const offerName = e.target.getAttribute(`nss-offer-name`);
          this.selFor(id, `[nss-selected-offer-name]`).forEach((el) => {
            el.innerHTML = offerName;
          });
          this.updateEvent(id);
          this.applyDiscountPreview();
        }
      }

      if (e.target.hasAttribute(`nss-qty-down`)) {
        const id = this.variantOf(e.target);
        const els = this.formEls(id);
        if (els.quantity) {
          let qty = parseInt(els.quantity.value);
          if (qty <= this.qtyBounds(els.quantity).min) return;
          qty -= 1;
          this.updateQtys(id, qty);
          this.updateEvent(id);
          this.applyDiscountPreview();
        }
      }

      if (e.target.hasAttribute(`nss-qty-up`)) {
        const id = this.variantOf(e.target);
        const els = this.formEls(id);
        if (els.quantity) {
          let qty = parseInt(els.quantity.value);
          if (qty >= this.qtyBounds(els.quantity).max) return;
          qty += 1;
          this.updateQtys(id, qty);
          this.updateEvent(id);
          this.applyDiscountPreview();
        }
      }
    }

    /* ----------------------------
       Quantity
    ---------------------------- */

    updateQtys(id, qty) {
      const els = this.formEls(id);
      if (!els.quantity) return;
      const bounds = this.qtyBounds(els.quantity);
      if (qty < bounds.min) qty = bounds.min;
      if (qty > bounds.max) qty = bounds.max;
      els.quantity.value = qty;
      // Steppers and displays render per variant per block (quantity block,
      // ATC row); the variant's quantity is shared, so keep them all in sync.
      this.selFor(id, `[nss-qty-down]`).forEach((el) => {
        el.disabled = qty <= bounds.min;
      });
      this.selFor(id, `[nss-qty-up]`).forEach((el) => {
        el.disabled = qty >= bounds.max;
      });
      this.selFor(id, `[nss-qty-display-actual]`).forEach((el) => {
        el.innerHTML = qty;
      });
      this.selFor(id, `[nss-qty-display-actual-label]`).forEach((el) => {
        el.innerHTML = qty > 1 ? `BOXES` : `BOX`;
      });
      this.selFor(id, `[nss-qty-display-total]`).forEach((el) => {
        el.innerHTML = qty * 12;
      });
    }

    /* ----------------------------
       One-time ATC
    ---------------------------- */

    atcBtnOTP(id) {
      const form = this.formEls(id).form;
      if (form) {
        // Direct-to-checkout: one-time, buy just this item, straight to checkout.
        // (window.NSS_DIRECT_CHECKOUT / NSS_BUILD_ADD_ITEMS live in nss-cart-template.js.)
        if (form.closest(`[data-direct-checkout]`)) {
          window.NSS_DIRECT_CHECKOUT?.(form, { otp: true });
          return;
        }
        // Add-on active → add both items in one call (one-time main + add-on per its mode).
        const items = window.NSS_BUILD_ADD_ITEMS?.(form, { otp: true });
        if (items) {
          fetch(window.Shopify.routes.root + "cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ items }),
          })
            .then(() => {
              window?.NSS_REFRESH_AND_OPEN_CART();
            })
            .catch((error) => {
              console.error("Error:", error);
            });
          return;
        }
        const formData = new FormData(form);
        formData.delete("selling_plan");
        fetch(window.Shopify.routes.root + "cart/add.js", {
          method: "POST",
          body: formData,
        })
          .then(() => {
            window?.NSS_REFRESH_AND_OPEN_CART();
          })
          .catch((error) => {
            console.error("Error:", error);
          });
      }
    }

    /* ----------------------------
       Discount preview (window.NSS_DISCOUNT)
    ---------------------------- */

    applyDiscountPreview() {
      const lib = window.NSS_DISCOUNT;
      if (!lib) return;

      const id = this.currentId;
      const els = this.formEls(id);
      const sel = (hook) => this.selFor(id, hook);
      // Plan options and discount context render inside the current variant's
      // panes — resolve section-wide since each child block has its own pane.
      const checked = this.sectionScope.querySelector(
        `[nss-variant-id="${id}"] [nss-selling-plan-id]:checked`
      );
      const ctxPane = this.sectionScope.querySelector(
        `nss-buybox-pane[nss-variant-id="${id}"][data-product-id]`
      );
      // Legacy variant-per-frequency: match discounts against the option's own
      // variant when present, else the pane's variant.
      const discountVariantId = (checked && checked.getAttribute(`data-variant-id`)) || id;
      const isSub = els.planSelect ? els.planSelect.value !== `` : true;
      const qty = els.quantity ? parseInt(els.quantity.value, 10) || 1 : 1;
      const cookieCode = lib.readCookie(`discount_code`);
      const ctx = (mode, lineCents, q, vId) => ({
        cookieCode,
        purchaseType: mode,
        productId: ctxPane ? ctxPane.getAttribute(`data-product-id`) : null,
        variantId: vId || discountVariantId,
        collectionIds: (ctxPane ? ctxPane.getAttribute(`data-collection-ids`) || `` : ``)
          .split(`,`)
          .filter(Boolean),
        quantity: q || qty,
        lineSubtotalCents: lineCents,
      });

      let subMatch = null;
      let otpMatch = null;

      if (checked) {
        const supplyDays = parseInt(checked.getAttribute(`data-supply-days`), 10);

        // ---- Subscription: price, "Subscribe & Save XX%", per-day, saving ----
        const compare = parseInt(checked.getAttribute(`data-compare-at-price`), 10);
        const base = parseInt(checked.getAttribute(`data-price`), 10);
        subMatch = lib.findMatch(ctx(`subscription`, (base || 0) * qty));
        let disp = base;
        let planName = checked.getAttribute(`nss-plan-name`) || ``;
        if (subMatch && compare && !isNaN(base)) {
          const r = lib.apply({
            compareCents: compare,
            priceCents: base,
            discounts: subMatch.chain,
          });
          if (r) {
            disp = r.newPriceCents;
            planName = planName.replace(/SAVE\s+\d+%/i, `SAVE ${r.totalPct}%`);
          }
        }
        if (!isNaN(base)) {
          sel(`[nss-selected-frequency-price]`).forEach(
            (el) => (el.innerText = this.currencyFormatter.format((qty * disp) / 100))
          );
          sel(`[nss-selected-plan-name-title]`).forEach((el) => (el.innerHTML = planName));
          // Only override per-day + "You're saving" when discounted; the base
          // handlers already keep them correct otherwise.
          if (subMatch && disp !== base) {
            if (supplyDays)
              sel(`[nss-selling-serving]`).forEach(
                (el) => (el.innerHTML = this.formatPerDay(disp, supplyDays))
              );
            if (compare)
              sel(`[nss-selling-saving-ammount]`).forEach(
                (el) =>
                  (el.innerHTML = `You’re saving ${this.currencyFormatter.format(
                    ((compare - disp) * qty) / 100
                  )}`)
              );
          }
        }

        // ---- One-time: price + per-day (when the discount applies to one-time) ----
        const otpCompare = parseInt(checked.getAttribute(`data-otp-compare-at-price`), 10);
        const otpBase = parseInt(checked.getAttribute(`data-otp-price`), 10);
        otpMatch = lib.findMatch(ctx(`onetime`, (otpBase || 0) * qty));
        if (otpMatch && !isNaN(otpBase)) {
          const r = lib.apply({
            compareCents: otpCompare || otpBase,
            priceCents: otpBase,
            discounts: otpMatch.chain,
          });
          if (r) {
            // Show the pre-discount one-time price as a strikethrough (use the
            // compare-at if higher, else the base one-time price).
            const otpStrikeUnit = Math.max(isNaN(otpCompare) ? 0 : otpCompare, otpBase);
            sel(`[nss-selected-otp-compare-at-price]`).forEach(
              (el) => (el.innerText = this.currencyFormatter.format((qty * otpStrikeUnit) / 100))
            );
            sel(`[nss-selected-otp-price]`).forEach(
              (el) => (el.innerText = this.currencyFormatter.format((qty * r.newPriceCents) / 100))
            );
            if (supplyDays)
              sel(`[nss-selected-otp-per-day]`).forEach(
                (el) => (el.innerText = this.money2(Math.round(r.newPriceCents / supplyDays) / 100))
              );
          }
        }
      }

      // ---- Per-option "/ Day" in the size-selector cards ----
      sel(`[nss-selling-plan-id]`).forEach((input) => {
        const label = input.closest(`label`);
        const span = label ? label.querySelector(`[nss-card-per-day]`) : null;
        if (!span) return;
        const perUnit = parseInt(input.getAttribute(`data-price`), 10);
        const sd = parseInt(input.getAttribute(`data-supply-days`), 10);
        if (isNaN(perUnit) || !sd) return;
        const cardQty = parseInt(input.getAttribute(`data-quantity`), 10) || 1;
        let pu = perUnit;
        const m = lib.findMatch(
          ctx(`subscription`, perUnit * cardQty, cardQty, input.getAttribute(`data-variant-id`))
        );
        if (m) {
          const r = lib.apply({
            compareCents: parseInt(input.getAttribute(`data-compare-at-price`), 10) || perUnit,
            priceCents: perUnit,
            discounts: m.chain,
          });
          if (r) pu = r.newPriceCents;
        }
        span.innerHTML = this.formatPerDay(pu, sd);
      });

      // Chip + pill reflect the currently-selected purchase mode
      const activeMatch = isSub ? subMatch : otpMatch;
      this.renderStandaloneDiscount(subMatch);
      this.renderDiscountChip(activeMatch);
      this.renderDiscountPill(activeMatch);
    }

    renderDiscountPill(match) {
      this.selFor(this.currentId, `[nss-discount-applied-pill]`).forEach((pill) => {
        pill.hidden = !match;
      });
    }

    renderStandaloneDiscount(match) {
      // The standalone price lives in the nss-buybox-price block — resolve it
      // across the section by variant id.
      const id = this.currentId;
      const group = this.sectionScope.querySelector(
        `[nss-variant-id="${id}"] [nss-standalone-group]`
      );
      if (!group) return;
      const savingsEl = group.querySelector(`[nss-standalone-savings]`);
      const priceEl = group.querySelector(`[nss-standalone-price]`);
      // Base (undiscounted) text is cached per variant so a later restore never
      // bleeds one variant's price onto another's group.
      if (this._baseStandalone[id] === undefined) {
        this._baseStandalone[id] = {
          savings: savingsEl ? savingsEl.textContent : null,
          price: priceEl ? priceEl.textContent : null,
        };
      }
      const base = this._baseStandalone[id];
      const sCompare = parseInt(group.getAttribute(`data-base-compare`), 10);
      const sPrice = parseInt(group.getAttribute(`data-base-price`), 10);
      if (match && sCompare && !isNaN(sPrice)) {
        const res = window.NSS_DISCOUNT.apply({
          compareCents: sCompare,
          priceCents: sPrice,
          discounts: match.chain,
        });
        if (res) {
          if (priceEl) priceEl.textContent = this.currencyFormatter.format(res.newPriceCents / 100);
          if (savingsEl) savingsEl.textContent = `SAVE ${res.totalPct}%`;
          return;
        }
      }
      if (savingsEl && base.savings !== null) savingsEl.textContent = base.savings;
      if (priceEl && base.price !== null) priceEl.textContent = base.price;
    }

    renderDiscountChip(match) {
      // The chip belongs to the cookie-coded discount when one is in the chain
      // (it applies last), else the automatic. Its "Show discount code" toggle
      // on the metaobject gates the chip.
      const disp = match && match.display;
      const show = !!(disp && disp.show_code);
      const label =
        disp && disp.code && disp.code.trim()
          ? disp.code.trim()
          : disp
            ? disp.display_name || ``
            : ``;
      this.selFor(this.currentId, `[nss-discount-chip]`).forEach((chip) => {
        const codeEl = chip.querySelector(`[nss-discount-code]`);
        if (show) {
          if (codeEl) codeEl.textContent = label;
          chip.hidden = false;
        } else {
          chip.hidden = true;
        }
      });
    }
  }

  customElements.define(elementName, NSSBuybox);
}
